import { ServiceItem } from '../../types';
import { SigeEinweisung } from '../sige-plan/types';
import { CompanyInfo } from '../../services/pdf/letterhead';
import {
  EinweisungPdfOptionen,
  EinweisungSeiten,
  generateEinweisungPdfMitSeiten,
  zaehleEinweisungSeiten,
} from '../../services/pdf/generatorEinweisung';
import {
  EinweisungBuchung,
  EinweisungBuchungsKontext,
  bucheEinweisung,
} from '../../services/einweisungBooking';
import { signedUrlFuerDokument } from '../../services/sigeEinweisungService';
import { einweisungAblageDateiname, einweisungAblageQuelle, einweisungPdfDateiname } from './einweisungAblage';

/**
 * Die beiden Anschluesse an die Nachbarmodule — PDF-Erzeugung und Verbuchung.
 *
 * WARUM diese Zwischenschicht ueberhaupt: beide Nachbarn WERFEN im Fehlerfall
 * (jsPDF stolpert an einer kaputten Unterschrifts-DataURL, der Buchungsdienst
 * an unbrauchbaren Daten). Ein solcher Fehler darf immer nur den EINEN Knopf
 * treffen, der ihn ausgeloest hat — nie die Ablage, nie einen halb
 * abgeschlossenen Vorgang. Hier wird er in ein Ergebnis verwandelt, das die
 * Oberflaeche anzeigen kann.
 *
 * ─── Zwei Seitenzahlen, zwei Aufgaben ──────────────────────────────────────
 * `schaetzeSeiten` (services/sigeEinweisung.ts) rechnet mit Zeichen je Zeile,
 * ist billig und faerbt die Anzeige bei JEDEM Tastendruck.
 * `zaehleEinweisungSeiten` setzt das Dokument wirklich und ist die
 * VERBINDLICHE Aussage — sie laeuft deshalb nur verzoegert und beim Export.
 */

export type { EinweisungPdfOptionen, EinweisungSeiten, EinweisungBuchungsKontext };

/**
 * Ergebnis eines gekapselten Aufrufs: entweder gelaufen oder mit Grund
 * gescheitert.
 *
 * Der Unterscheider ist bewusst eine ZEICHENKETTE und kein `ok: boolean`:
 * dieses Projekt uebersetzt ohne `strictNullChecks`, und dort grenzt TypeScript
 * eine Union ueber einen booleschen Unterscheider nicht zuverlaessig ein — der
 * Zugriff auf `fehler` im Fehlerzweig galt dann als Tippfehler.
 */
export type Anschluss<T> =
  | { status: 'ok'; wert: T }
  | { status: 'fehler'; fehler: string };

/**
 * Erzeugt das Einweisungsblatt, laedt es herunter und meldet die tatsaechliche
 * Seitenzahl zurueck.
 *
 * `generateEinweisungPdfMitSeiten` statt Erzeugen + Zaehlen: das Dokument wird
 * so genau EINMAL gesetzt.
 *
 * BUCHT NICHT. Das ist der tragende Unterschied zum SiGe-Plan, der beim Export
 * verbucht: ein Einweisungsblatt wird mehrfach gedruckt (Aushang, Ordner,
 * Nachzuegler), und jeder weitere Druck wuerde die LV-Position ein zweites Mal
 * belasten. Gebucht wird genau einmal, beim Abschliessen bzw. nach dem
 * Papier-Upload (siehe darfBuchungAnbieten in einweisungLogic.ts).
 */
export const exportiereEinweisungPdf = (
  einweisung: SigeEinweisung,
  company: CompanyInfo,
  optionen?: EinweisungPdfOptionen,
): Anschluss<EinweisungSeiten> => {
  try {
    const { doc, seiten } = generateEinweisungPdfMitSeiten(einweisung, company, optionen);
    // Der Name kommt aus einweisungAblage — Download und Projektablage
    // muessen dieselbe Datei gleich nennen.
    doc.save(einweisungPdfDateiname(einweisung));
    return { status: 'ok', wert: seiten };
  } catch (e: any) {
    return { status: 'fehler', fehler: `Das Einweisungsblatt konnte nicht erzeugt werden: ${e?.message || String(e)}` };
  }
};

/** Die Datei, die in die Projektablage geht, samt Namen. */
export interface EinweisungAblageDatei {
  blob: Blob;
  dateiname: string;
}

/**
 * Baut die Datei fuer die Projektablage (Stufe 1A): bei einer digital
 * gefuehrten Einweisung das gesetzte Blatt, bei einer Papier-Einweisung der
 * hinterlegte Scan aus dem Bucket `sige-einweisungen` — das erzeugte PDF waere
 * dort ein leeres Blatt mit Kopfdaten, das Original ist der Zettel.
 *
 * Schreibt NICHTS und bucht nicht; wirft nie (Muster dieser Datei). Der Scan
 * wird ueber eine signierte URL geholt — der Bucket ist privat, und genau
 * dieselbe Policy, die das Ansehen erlaubt, erlaubt auch das Holen hier.
 */
export const baueEinweisungAblageDatei = async (
  einweisung: SigeEinweisung,
  company: CompanyInfo,
  optionen?: EinweisungPdfOptionen,
): Promise<Anschluss<EinweisungAblageDatei>> => {
  const dateiname = einweisungAblageDateiname(einweisung);
  if (einweisungAblageQuelle(einweisung) === 'scan') {
    try {
      const url = await signedUrlFuerDokument(einweisung.dokument_pfad || '');
      const antwort = await fetch(url);
      if (!antwort.ok) throw new Error(`Der Scan konnte nicht geladen werden (HTTP ${antwort.status}).`);
      return { status: 'ok', wert: { blob: await antwort.blob(), dateiname } };
    } catch (e: unknown) {
      return { status: 'fehler', fehler: `Der hinterlegte Scan konnte nicht geholt werden: ${e instanceof Error ? e.message : String(e)}` };
    }
  }
  try {
    const { doc } = generateEinweisungPdfMitSeiten(einweisung, company, optionen);
    return { status: 'ok', wert: { blob: doc.output('blob') as Blob, dateiname } };
  } catch (e: unknown) {
    return { status: 'fehler', fehler: `Das Einweisungsblatt konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}` };
  }
};

/**
 * Die verbindliche Seitenzahl — `null`, wenn der Satz nicht durchlief.
 *
 * `null` ist kein Fehler, den der Anwender sehen muss: faellt die genaue Zahl
 * aus, bleibt die Schaetzung stehen. Eine Fehlermeldung an dieser Stelle waere
 * eine Meldung ueber eine ANZEIGE — der Export sagt es deutlich genug, falls
 * wirklich etwas kaputt ist.
 */
export const zaehleSeitenSicher = (
  einweisung: SigeEinweisung,
  company: CompanyInfo,
  optionen?: EinweisungPdfOptionen,
): EinweisungSeiten | null => {
  try {
    return zaehleEinweisungSeiten(einweisung, company, optionen);
  } catch {
    return null;
  }
};

/**
 * Rechnet die Buchung aus — ohne zu schreiben.
 *
 * Das Schreiben bleibt beim Aufrufer (`onUpdate` ins Projekt,
 * `markiereGebucht` an der Einweisung), damit der Riegel gegen die
 * Doppelbuchung an einer Stelle sitzt und nicht in dieser Kapsel.
 *
 * `null` als Wert heisst: nichts zu buchen (Position nicht gefunden). Das ist
 * kein Fehler und bekommt deshalb `status: 'ok'`.
 */
export const rechneEinweisungsBuchung = (
  services: ServiceItem[],
  serviceId: string,
  menge: number,
  kontext: EinweisungBuchungsKontext,
): Anschluss<EinweisungBuchung | null> => {
  try {
    return { status: 'ok', wert: bucheEinweisung(services, serviceId, menge, kontext) };
  } catch (e: any) {
    return { status: 'fehler', fehler: `Die Leistung konnte nicht gebucht werden: ${e?.message || String(e)}` };
  }
};
